import { alpha, Box, Typography, useTheme } from "@mui/material";
import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { SubtypeBreakdown } from "../../../store/services/salesManagerService";
import { getWorkItemColor } from "../../../utils/workItemUtils";
import {
  getSubtypeCounts,
  SUBTYPE_LABELS,
  SUBTYPE_ORDER,
  SUBTYPE_TO_WORK_ITEM_TYPE,
} from "../shared/breakdown";
import { WorkItemSubtype } from "../../../store/services/salesManagerService";

interface SubtypeBarChartProps {
  /** En specifik SubtypeBreakdown (t.ex. Breakdown.Total.Communication för
   * Planering eller Breakdown.Total.Followup för Mötesbokning). Anroparen
   * väljer kategori — diagrammet är kategori-agnostiskt. */
  subtypeBreakdown: SubtypeBreakdown;
  /** Vilka subtyper som ska visas och i vilken ordning. Default = alla. */
  subtypeOrder?: WorkItemSubtype[];
  height?: number;
}

/**
 * Horisontell bar chart som visar fördelningen av aktiviteter per subtyp
 * (Email, Telefon, SMS, Digitalt möte, Fysiskt möte). Staplad per status —
 * Slutförda vs Aktiva vs Overdue.
 */
const SubtypeBarChart: React.FC<SubtypeBarChartProps> = ({
  subtypeBreakdown,
  subtypeOrder = SUBTYPE_ORDER,
  height = 280,
}) => {
  const theme = useTheme();

  const data = useMemo(() => {
    return subtypeOrder.map((subtype) => {
      const counts = getSubtypeCounts(subtypeBreakdown, subtype);
      return {
        subtype,
        label: SUBTYPE_LABELS[subtype],
        Completed: counts.Completed - (counts.Skipped ?? 0), // genomförda på riktigt
        Skipped: counts.Skipped ?? 0,
        Active: counts.Active - counts.Overdue, // på-spår aktiva
        Overdue: counts.Overdue,
        color: getWorkItemColor(SUBTYPE_TO_WORK_ITEM_TYPE[subtype]),
      };
    });
  }, [subtypeBreakdown, subtypeOrder]);

  const hasData = data.some(
    (d) => d.Completed + d.Skipped + d.Active + d.Overdue > 0
  );

  if (!hasData) {
    return (
      <Box
        sx={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Ingen data i vald period
        </Typography>
      </Box>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 8, right: 16, left: 16, bottom: 0 }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={theme.palette.divider}
          horizontal={false}
        />
        <XAxis
          type="number"
          tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
          tickLine={false}
          axisLine={{ stroke: theme.palette.divider }}
          allowDecimals={false}
        />
        <YAxis
          type="category"
          dataKey="label"
          tick={{ fontSize: 12, fill: theme.palette.text.primary }}
          tickLine={false}
          axisLine={{ stroke: theme.palette.divider }}
          width={110}
        />
        <Tooltip
          contentStyle={{
            borderRadius: 8,
            border: `1px solid ${theme.palette.divider}`,
            fontSize: 12,
          }}
          cursor={{ fill: theme.palette.action.hover }}
        />
        <Legend
          iconType="circle"
          wrapperStyle={{ fontSize: 12 }}
          verticalAlign="top"
          height={28}
        />
        <Bar
          dataKey="Completed"
          stackId="status"
          name="Genomförda"
          fill={theme.palette.success.main}
          radius={[0, 0, 0, 0]}
        >
          {data.map((entry) => (
            <Cell key={`completed-${entry.subtype}`} />
          ))}
        </Bar>
        <Bar
          dataKey="Skipped"
          stackId="status"
          name="Skippade"
          fill={alpha(theme.palette.success.main, 0.35)}
        />
        <Bar
          dataKey="Active"
          stackId="status"
          name="Aktiva"
          fill={theme.palette.info.main}
        />
        <Bar
          dataKey="Overdue"
          stackId="status"
          name="Overdue"
          fill={theme.palette.error.main}
          radius={[0, 4, 4, 0]}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

export default SubtypeBarChart;
